import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { studioApi } from '../data/studioApi';
import { automationsLive, highestPlan, planAllows } from '../enums/automations';
import useStableActions from './useStableActions';

// Which gate each field of a `plan_upgrade_required` answer stands for.
const FIELD_GATES = { status: 'activate', 'dm.follow_gate.enabled': 'follow_gate', 'dm.email_capture.enabled': 'email_capture', 'dm.reminder.enabled': 'reminder' };

// The Direct Messages home (contract 2.14.0, GET /automations/overview): what the sidebar's inbox
// badge, the home and the editor's upgrade marks read. It follows the active profile and the plan;
// a late answer for another profile is dropped.
export default function useAutomations({ profileId, plan, openUpgrade }) {
  const [overview, setOverview] = useState(null);
  // `off` (no profile yet) · `loading` · `ready` · `error` (the first read failed).
  const [status, setStatus] = useState('off');
  const scope = useRef(profileId);
  scope.current = profileId;

  const refresh = useCallback(async () => {
    const id = scope.current;
    if (!automationsLive || !id) return null;
    try {
      const next = await studioApi.automationsOverview({ profile_id: id });
      if (scope.current !== id) return null;
      setOverview(next);
      setStatus('ready');
      return next;
    } catch {
      if (scope.current === id) setStatus(state => (state === 'ready' ? state : 'error'));
      return null;
    }
  }, []);

  useEffect(() => {
    setOverview(null);
    if (!automationsLive || !profileId) { setStatus('off'); return; }
    setStatus('loading');
    refresh();
  }, [profileId, refresh]);
  // A new plan moves the limit and the options; the screen stays as it is meanwhile.
  useEffect(() => { refresh(); }, [plan, refresh]);

  const gates = overview?.plan_gates;
  // The plan that would let this automation activate, from the fields core refused.
  const planForFields = fields => highestPlan(Object.keys(fields).map(field => gates?.[FIELD_GATES[field] || (field === 'trigger.media_scope' ? 'media_scope_next' : 'activate')] || 'standard'));
  const actions = useStableActions({ refresh, planForFields, upgrade: (target, reason = null) => openUpgrade(target, reason) });
  return useMemo(() => ({
    enabled: automationsLive,
    status,
    overview,
    unread: overview?.inbox.unread_conversations ?? 0,
    connection: overview?.connection ?? null,
    connected: overview?.connection?.status === 'connected',
    gates: gates ?? null,
    locked: option => !!gates && !planAllows(plan, gates[option]),
    ...actions,
  }), [status, overview, gates, plan, actions]);
}
